import React, { useState, useCallback, useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  Box,
  Text,
  VStack,
  Input,
  Flex,
  Icon,
  useToast,
  List,
  ListItem,
  Image,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import { brandApi } from '../../../services/api/brand';
import { deliverableApi } from '../../../services/api/deliverable';
import { contentPortfolioApi } from '../../../services/api/contentPortfolio';
import { BrandSearchResultDto } from '@foodfluence/shared';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { uploadWithProgress } from '../../../utils/contentUploadUtils';
import { detectDeliverableTypeFromUrl } from '../../../utils/urlUtils';
import { isVideoUrl } from '../../../utils/fileUtils';
import mime from 'mime/lite';
import ReviewContentModalConfirmation from '../../collabs/components/ReviewContentModalConfirmation';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';

interface SubmitLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type ModalState = 
  | { type: 'input' }
  | { type: 'downloading' }
  | { type: 'preview'; contentUrl: string; contentType: 'image' | 'video'; postLink: string; brand: string }
  | { type: 'uploading'; progress: number }
  | { type: 'success' };

const SubmitLinkModal: React.FC<SubmitLinkModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [link, setLink] = useState('');
  const [brandName, setBrandName] = useState('');
  const [suggestions, setSuggestions] = useState<BrandSearchResultDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandSearchResultDto | null>(null);
  const [isBrandSelected, setIsBrandSelected] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ type: 'input' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchBrandSuggestions = async (input: string) => {
    if (input.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await brandApi.searchBrands(input);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching brand suggestions:', error);
      setSuggestions([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchBrands = useCallback(debounce(fetchBrandSuggestions, 300), []);

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandName(value);
    setIsBrandSelected(false);
    setSelectedBrand(null);
    debouncedFetchBrands(value);
  };

  const handleSuggestionClick = (brand: BrandSearchResultDto) => {
    setSelectedBrand(brand);
    setBrandName(brand.name);
    setIsBrandSelected(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleClose = () => {
    setLink('');
    setBrandName('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedBrand(null);
    setIsBrandSelected(false);
    setModalState({ type: 'input' });
    setIsSubmitting(false); // Reset loading state
    onClose();
  };

  const handleSubmit = async () => {
    if (!link.trim() || !isBrandSelected || !selectedBrand) {
      toast({
        title: 'Missing information',
        description: 'Please select a brand from the suggestions and enter a link',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Detect deliverable type from URL
    const deliverableType = detectDeliverableTypeFromUrl(link);
    
    if (!deliverableType) {
      toast({
        title: 'Unsupported link',
        description: 'Unable to detect content type from URL',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Download content
    setModalState({ type: 'downloading' });

    try {
      let contentUrl: string;
      let contentType: 'image' | 'video';

      if (deliverableType === 'instagramStories') {
        const result = await deliverableApi.downloadInstagramStory(link, 'UGC_CONTENT', influencerData?.id);
        contentUrl = result.storyUrl;
        contentType = isVideoUrl(result.storyUrl) ? 'video' : 'image';
      } else if (deliverableType === 'instagramReels') {
        const result = await deliverableApi.downloadInstagramReel(link, 'UGC_CONTENT', influencerData?.id);
        contentUrl = result.reelUrl;
        contentType = 'video';
      } else if (deliverableType === 'instagramPosts') {
        const result = await deliverableApi.downloadInstagramPost(link, 'UGC_CONTENT', influencerData?.id);
        contentUrl = result.postUrl;
        contentType = result.contentType;
      } else if (deliverableType === 'tiktoks') {
        const result = await deliverableApi.downloadTikTokPost(link, 'UGC_CONTENT', influencerData?.id);
        contentUrl = result.postUrl;
        contentType = 'video';
      } else {
        throw new Error(`Unsupported deliverable type: ${deliverableType}`);
      }

      // Show preview modal
      setModalState({
        type: 'preview',
        contentUrl,
        contentType,
        postLink: link,
        brand: selectedBrand.name,
      });
      setIsSubmitting(false); // Reset loading state when showing preview
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: 'There was an error downloading your content. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setModalState({ type: 'input' });
    }
  };

  const handleConfirmSubmission = async () => {
    if (modalState.type !== 'preview') return;

    const { contentUrl, contentType, postLink, brand } = modalState;

    setIsSubmitting(true);

    try {
      const urlWithoutQuery = contentUrl.split('?')[0];
      const detectedMimeType = mime.getType(urlWithoutQuery);
      const mimeType = detectedMimeType || (contentType === 'video' ? 'video/mp4' : 'image/jpeg');
      const fileExtension = (detectedMimeType && mime.getExtension(detectedMimeType)) || (contentType === 'video' ? 'mp4' : 'jpg');

      // Create portfolio record (pending) and receive the signed upload URL in one call
      const { portfolioId, uploadUrl } = await contentPortfolioApi.createPortfolioContent({
        brand,
        postLink,
        contentType,
        fileExtension,
        mimeType,
      }, influencerData.id);

      // Download content from external URL and upload to Firebase Storage
      const response = await fetch(contentUrl);
      const blob = await response.blob();
      const file = new File([blob], `upload.${fileExtension}`, { type: mimeType });

      await uploadWithProgress(uploadUrl!, file, () => {});

      // Confirm the upload so the record becomes visible
      await contentPortfolioApi.confirmUpload(portfolioId, influencerData.id);

      // Success!
      toast({
        title: 'Content added to library',
        description: 'Your content has been successfully added to your portfolio',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your content. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false); // Re-enable the button on error
    }
  };

  const handleRetry = () => {
    setModalState({ type: 'input' });
  };

  const isDownloading = modalState.type === 'downloading';
  const isSubmitEnabled = link.trim() !== '' && isBrandSelected && !isDownloading;

  // Show preview modal if in preview state
  if (modalState.type === 'preview') {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
        <ModalOverlay />
        <ReviewContentModalConfirmation
          isOpen={isOpen}
          onClose={handleClose}
          contentUrl={modalState.contentUrl}
          contentType={modalState.contentType}
          handleSubmitStory={handleConfirmSubmission}
          handleRetry={handleRetry}
          isLoading={isSubmitting}
        />
      </Modal>
    );
  }

  const platformChip = (icon: React.ElementType, label: string) => (
    <Flex align="center" gap={2} h="36px" px={3} bg="white" borderRadius="full" border="1px solid" borderColor={ff.border}>
      <Icon as={icon} color={ff.ink} boxSize={4} />
      <Text fontSize="14px" fontWeight={500} color={ff.ink}>
        {label}
      </Text>
    </Flex>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit link"
      cancelLabel="Cancel"
      primary={{
        label: 'Submit',
        onClick: () => void handleSubmit(),
        tone: 'lime',
        isDisabled: !isSubmitEnabled,
        isLoading: isDownloading,
      }}
    >
      <VStack spacing={5} align="stretch">
        <FormControl position="relative">
          <FormLabel>Brand name</FormLabel>
          <Flex align="center" position="relative">
            {selectedBrand && selectedBrand.icon && (
              <Box position="absolute" left={3} zIndex={2}>
                <Image src={selectedBrand.icon} alt={selectedBrand.name} boxSize="24px" borderRadius="6px" objectFit="contain" />
              </Box>
            )}
            <Input
              value={brandName}
              onChange={handleBrandNameChange}
              placeholder="Start typing brand name..."
              borderColor={isBrandSelected ? ff.ink : undefined}
              pl={selectedBrand && selectedBrand.icon ? '44px' : '16px'}
              fontSize="16px"
            />
          </Flex>

          {showSuggestions && suggestions.length > 0 && (
            <List
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              boxShadow="0 8px 24px rgba(14, 14, 14, 0.08)"
              borderRadius="14px"
              maxH="200px"
              overflowY="auto"
              zIndex={1000}
              mt={2}
              border="1px solid"
              borderColor={ff.line}
            >
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  minH="52px"
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: ff.grey }}
                  borderTop={index === 0 ? 'none' : '1px solid'}
                  borderColor={ff.line}
                  onClick={() => handleSuggestionClick(suggestion)}
                  display="flex"
                  alignItems="center"
                  gap={3}
                >
                  {suggestion.icon && (
                    <Image
                      src={suggestion.icon}
                      alt={suggestion.name}
                      boxSize="32px"
                      borderRadius="8px"
                      objectFit="contain"
                      flexShrink={0}
                    />
                  )}
                  <Text fontWeight={500} fontSize="15px" color={ff.ink}>
                    {suggestion.name}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>Content link</FormLabel>
          <Input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste Instagram or TikTok link"
            fontSize="16px"
          />
        </FormControl>

        <Box bg={ff.grey} borderRadius="20px" p={4}>
          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            Supported platforms
          </Text>
          <Flex gap={2}>
            {platformChip(FaInstagram, 'Instagram')}
            {platformChip(FaTiktok, 'TikTok')}
          </Flex>
        </Box>

        <Box border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Text fontFamily="display" fontSize="15px" fontWeight={600} color={ff.ink} mb={2}>
            Tips
          </Text>
          <VStack align="stretch" spacing={1}>
            <Text fontSize="13px" color={ff.body}>
              • Make sure the post is publicly visible
            </Text>
            <Text fontSize="13px" color={ff.body}>
              • Copy the full URL from your browser or app
            </Text>
            <Text fontSize="13px" color={ff.body}>
              • Ensure the content clearly features the brand
            </Text>
          </VStack>
        </Box>
      </VStack>
    </ModalShell>
  );
};

export default SubmitLinkModal;
